import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

type Theme = 'dark' | 'light';
type ColorblindMode = 'none' | 'protanopia' | 'deuteranopia' | 'tritanopia';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  colorblindMode: ColorblindMode;
  setColorblindMode: (mode: ColorblindMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'bigE-theme';
const COLORBLIND_STORAGE_KEY = 'bigE-colorblind-mode';

// Color accessibility options for UI
export const COLOR_ACCESSIBILITY_OPTIONS: { value: ColorblindMode; label: string; description: string }[] = [
  { value: 'none', label: 'Standard', description: 'Default color palette' },
  { value: 'protanopia', label: 'Protanopia', description: 'Optimized for red-green color vision' },
  { value: 'deuteranopia', label: 'Deuteranopia', description: 'Optimized for red-green color vision' },
  { value: 'tritanopia', label: 'Tritanopia', description: 'Optimized for blue-yellow color vision' },
];

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'dark';
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    // Migrate 'system' to 'dark' for existing users
    if (stored === 'system' || !stored) return 'dark';
    return stored as Theme;
  });

  const [colorblindMode, setColorblindModeState] = useState<ColorblindMode>(() => {
    if (typeof window === 'undefined') return 'none';
    const stored = localStorage.getItem(COLORBLIND_STORAGE_KEY);
    return (stored as ColorblindMode) || 'none';
  });

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('dark', 'light');
    root.classList.add(theme);
  }, [theme]);

  // Apply colorblind mode to document
  useEffect(() => {
    const root = document.documentElement;
    // Remove all colorblind classes
    root.classList.remove('cb-protanopia', 'cb-deuteranopia', 'cb-tritanopia');
    // Add the selected one if not 'none'
    if (colorblindMode !== 'none') {
      root.classList.add(`cb-${colorblindMode}`);
    }
  }, [colorblindMode]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
  };

  const setColorblindMode = (mode: ColorblindMode) => {
    setColorblindModeState(mode);
    localStorage.setItem(COLORBLIND_STORAGE_KEY, mode);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, colorblindMode, setColorblindMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
